import React, { useState, useMemo } from 'react';
import Page from '../components/Page';
import { useActiveShift, useShiftsList, openShift, closeShift } from '../controllers/financial.controller';
import { useStoreSettings, usePrintSettings } from '../controllers/settings.controller';
import { getInvoiceOrders } from '../controllers/invoices.controller';
import { printReceipt } from '../helpers/PrinterHelper';
import { setDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import FinancialReportsPrintModal from '../components/FinancialReportsPrintModal';
import { getUserDetailsInLocalStorage } from '../helpers/UserDetails';
import { 
  IconEye, 
  IconPrinter, 
  IconCash, 
  IconCheck, 
  IconX, 
  IconAlertTriangle,
  IconCoins,
  IconClock,
  IconUser,
  IconRefresh,
  IconArrowUpRight,
  IconArrowDownRight,
  IconListCheck,
  IconSearch,
  IconPlus,
  IconMinus,
  IconCalculator,
  IconReceipt,
  IconPlayerPlay,
  IconPlayerStop
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

export default function FinancialShiftPage() {
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'closed' | 'reports'
  const [txFilter, setTxFilter] = useState('all');
  const [selectedTx, setSelectedTx] = useState(new Set());

  // Close shift modal state
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [closeShiftMode, setCloseShiftMode] = useState('notes'); // 'quick' | 'notes'
  const [closingDenoms, setClosingDenoms] = useState({ 1000: 0, 500: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0 });
  const [closingCashManual, setClosingCashManual] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [isClosingSubmitting, setIsClosingSubmitting] = useState(false);

  // Opening shift state
  const [openShiftMode, setOpenShiftMode] = useState('notes'); // 'quick' | 'notes'
  const [openingDenoms, setOpeningDenoms] = useState({ 1000: 0, 500: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0 });
  const [openingCashManual, setOpeningCashManual] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [isOpeningSubmitting, setIsOpeningSubmitting] = useState(false);

  // Selected shift for viewing detailed modal
  const [selectedShiftForDetails, setSelectedShiftForDetails] = useState(null);

  // Drill-down transactions modal (e.g., when clicking 👁 on payment methods)
  const [drillModal, setDrillModal] = useState({ isOpen: false, title: '', items: [], filter: '' });
  const [drillSearch, setDrillSearch] = useState('');

  // Print report modal state
  const [printModalConfig, setPrintModalConfig] = useState({ isOpen: false, reportType: 'shift_close', shift: null });

  // Queries
  const { shift, summary, isLoading, mutate } = useActiveShift();
  const { shifts: closedShifts, isLoading: isLoadingClosed, mutate: mutateClosed } = useShiftsList();
  const { data: storeSettings } = useStoreSettings();
  const { data: printSettings } = usePrintSettings();
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  const currency = storeSettings?.currency || "NPR";
  const currentUser = getUserDetailsInLocalStorage() || {};
  const staffProfileName = currentUser?.name || currentUser?.username || currentUser?.email || "Authorized Cashier";
  const staffDesignation = currentUser?.designation || currentUser?.role || "Staff Operator";

  const denominationList = [1000, 500, 100, 50, 20, 10, 5, 2, 1];
  const quickPresets = [500, 1000, 2000, 5000, 10000, 20000];

  // Calculate opening cash total
  const calculatedOpeningCash = useMemo(() => {
    if (openShiftMode === 'quick' && openingCashManual !== '') {
      return parseFloat(openingCashManual) || 0;
    }
    let sum = 0;
    Object.keys(openingDenoms).forEach(k => {
      sum += Number(k) * (Number(openingDenoms[k]) || 0);
    });
    return sum;
  }, [openingDenoms, openShiftMode, openingCashManual]);

  // Calculate closing cash total
  const calculatedClosingCash = useMemo(() => {
    if (closeShiftMode === 'quick' && closingCashManual !== '') {
      return parseFloat(closingCashManual) || 0;
    }
    let sum = 0;
    Object.keys(closingDenoms).forEach(k => {
      sum += Number(k) * (Number(closingDenoms[k]) || 0);
    });
    return sum;
  }, [closingDenoms, closeShiftMode, closingCashManual]);

  const handleDenomChange = (val, qty, isClose = false) => {
    const parsed = Math.max(0, parseInt(qty) || 0);
    if (isClose) {
      setClosingDenoms(prev => ({ ...prev, [val]: parsed }));
    } else {
      setOpeningDenoms(prev => ({ ...prev, [val]: parsed }));
    }
  };

  const handleDenomStep = (val, delta, isClose = false) => {
    if (isClose) {
      setClosingDenoms(prev => {
        const cur = Number(prev[val] || 0);
        return { ...prev, [val]: Math.max(0, cur + delta) };
      });
    } else {
      setOpeningDenoms(prev => {
        const cur = Number(prev[val] || 0);
        return { ...prev, [val]: Math.max(0, cur + delta) };
      });
    }
  };

  const handleOpenShift = async () => {
    if (calculatedOpeningCash <= 0 && !window.confirm("Open shift with 0 opening cash?")) return;
    try {
      setIsOpeningSubmitting(true);
      await openShift(calculatedOpeningCash, openingDenoms, openingNote);
      toast.success("Shift opened successfully!");
      mutate();
    } catch (err) {
      toast.error(err.message || "Failed to open shift");
    } finally {
      setIsOpeningSubmitting(false);
    }
  };

  // Active shift calculations
  const openingCash = Number(shift?.opening_cash || 0);
  const payments = summary?.payments || [];
  const expenses = summary?.expenses || [];
  const invoices = summary?.invoices || {};
  const transactions = summary?.transactions || [];

  const cashPaymentObj = payments.find(p => (p.payment_method || '').toLowerCase() === 'cash');
  const cashSales = Number(cashPaymentObj?.total || 0);
  const otherCashIn = 0;
  const cashExpObj = expenses.find(e => (e.payment_method || '').toLowerCase() === 'cash');
  const cashExpenses = Number(cashExpObj?.total || 0);
  const cashPurchases = 0;
  const cashRefunds = 0;
  const cashWithdrawals = 0;
  const cashDeposits = 0;

  // Expected drawer cash
  const expectedDrawerCash = openingCash + cashSales + otherCashIn - cashExpenses - cashPurchases - cashRefunds - cashWithdrawals + cashDeposits;
  const actualDrawerCash = calculatedClosingCash;
  const cashDifference = actualDrawerCash - expectedDrawerCash;

  const isBalanced = Math.abs(cashDifference) < 0.01;
  const isShort = cashDifference < -0.01;
  const isOver = cashDifference > 0.01;

  const handleCloseShiftSubmit = async () => {
    try {
      setIsClosingSubmitting(true);
      await closeShift(
        shift.id,
        actualDrawerCash,
        closingDenoms,
        expectedDrawerCash,
        cashDifference,
        closingNotes
      );
      toast.success("Shift closed and drawer reconciled successfully!");
      setIsCloseModalOpen(false);
      mutate();
      mutateClosed();
    } catch (err) {
      toast.error(err.message || "Failed to close shift");
    } finally {
      setIsClosingSubmitting(false);
    }
  };

  // Filter shift transactions
  const filteredTransactions = useMemo(() => {
    if (txFilter === 'all') return transactions;
    if (txFilter === 'sales') return transactions.filter(t => t.type === 'Sale');
    if (txFilter === 'cash') return transactions.filter(t => (t.payment_method || '').toLowerCase() === 'cash');
    if (txFilter === 'qr') return transactions.filter(t => (t.payment_method || '').toLowerCase().includes('qr') || (t.payment_method || '').toLowerCase().includes('upi'));
    if (txFilter === 'card') return transactions.filter(t => (t.payment_method || '').toLowerCase().includes('card'));
    if (txFilter === 'due') return transactions.filter(t => Number(t.due_amount || 0) > 0);
    if (txFilter === 'expenses') return transactions.filter(t => t.type === 'Expense');
    if (txFilter === 'discount') return transactions.filter(t => Number(t.discount || 0) > 0);
    if (txFilter === 'complimentary') return transactions.filter(t => (t.ref_no || '').includes('COMP') || t.is_complementary);
    if (txFilter === 'refunds') return transactions.filter(t => t.type === 'Refund');
    return transactions;
  }, [transactions, txFilter]);

  const toggleSelectTx = (id) => {
    const s = new Set(selectedTx);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelectedTx(s);
  };

  const toggleSelectAll = () => {
    if (selectedTx.size === filteredTransactions.length) {
      setSelectedTx(new Set());
    } else {
      setSelectedTx(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  // Open invoice detailed breakdown modal
  const handleViewInvoice = async (tx) => {
    try {
      setIsLoadingInvoice(true);
      toast.loading("Loading transaction details...");
      const invoiceId = tx.invoice_id || tx.id || tx.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders:", err);
      }
      toast.dismiss();

      setViewingInvoice({
        ...tx,
        ...(orderData || {}),
        invoiceId: invoiceId,
        invoiceNo: tx.ref_no || tx.invoice_no || orderData?.invoice_no || `INV-${invoiceId}`
      });
    } catch (err) {
      toast.dismiss();
      console.error("Error viewing invoice:", err);
      setViewingInvoice({
        ...tx,
        orders: [],
        invoiceId: tx.invoice_id || tx.id,
        invoiceNo: tx.ref_no || tx.invoice_no || `INV-${tx.id}`
      });
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // Open Payment method drill-down modal
  const openPaymentDrillDown = (p) => {
    const pMethod = (p.payment_method || '').toLowerCase();
    const matched = transactions.filter(t => (t.payment_method || '').toLowerCase() === pMethod);
    setDrillModal({
      isOpen: true,
      title: `${p.payment_method} Payment Transactions`,
      filter: p.payment_method,
      items: matched,
      total: p.total
    });
    setDrillSearch('');
  };

  // Filtered drill modal items
  const filteredDrillItems = useMemo(() => {
    if (!drillModal.items) return [];
    if (!drillSearch.trim()) return drillModal.items;
    const q = drillSearch.toLowerCase();
    return drillModal.items.filter(it => 
      (it.ref_no && String(it.ref_no).toLowerCase().includes(q)) ||
      (it.invoice_no && String(it.invoice_no).toLowerCase().includes(q)) ||
      (it.customer_name && String(it.customer_name).toLowerCase().includes(q)) ||
      (it.payment_method && String(it.payment_method).toLowerCase().includes(q)) ||
      (it.amount && String(it.amount).includes(q))
    );
  }, [drillModal.items, drillSearch]);

  // Print thermal receipt for single transaction
  const handlePrintTransaction = async (tx) => {
    try {
      toast.loading("Preparing receipt...");
      const invoiceId = tx.invoice_id || tx.id || tx.raw_id;
      let orderData = null;
      try {
        const res = await getInvoiceOrders(invoiceId);
        if (res && res.status === 200) {
          orderData = res.data;
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders, using transaction record:", err);
      }
      toast.dismiss();

      const ordersArr = orderData?.orders || [];
      let cartItems = [];

      for (const o of ordersArr) {
        (o.items || []).forEach(i => {
          cartItems.push({
            ...i,
            title: i.item_title || i.title || "Item",
            addons_ids: i?.addons?.length > 0 ? i.addons.map(a => a.id) : [],
            variant: i.variant_id ? { id: i.variant_id, title: i.variant_title, price: i.variant_price } : null
          });
        });
      }

      if (cartItems.length === 0) {
        cartItems.push({
          title: tx.item_name || tx.ref_no || `Invoice #${invoiceId}`,
          quantity: tx.quantity || 1,
          price: Number(tx.amount || 0)
        });
      }

      const billFormat = printSettings?.bill_format || localStorage.getItem("bill_format") || "retail_tax_invoice";
      const pageFormat = printSettings?.page_format || localStorage.getItem("page_format") || "58";

      const receiptDetails = {
        cartItems,
        customerType: "CUSTOMER",
        customer: {
          id: tx.customer_id || "Walk-in",
          name: tx.customer_name || "Walk-in Customer"
        },
        tableId: null,
        currency: currency,
        storeSettings: storeSettings || {},
        printSettings: printSettings || {},
        billFormat,
        pageFormat,
        itemsTotal: Number(orderData?.subtotal !== undefined ? orderData.subtotal : (tx.amount || 0)),
        taxTotal: Number(orderData?.taxTotal !== undefined ? orderData.taxTotal : (tx.tax_total || 0)),
        serviceChargeTotal: Number(orderData?.serviceChargeTotal || 0),
        payableTotal: Number(orderData?.total !== undefined ? orderData.total : (tx.amount || 0)),
        storedDiscount: Number(orderData?.discount !== undefined ? orderData.discount : (tx.discount || 0)),
        roundOff: Number(orderData?.roundOff !== undefined ? orderData.roundOff : (tx.round_off || 0)),
        paidAmount: Number(orderData?.paidAmount !== undefined ? orderData.paidAmount : (tx.amount || 0)),
        dueAmount: Number(orderData?.dueAmount !== undefined ? orderData.dueAmount : (tx.due_amount || 0)),
        tokenNo: tx.token_no,
        orderId: String(invoiceId),
        created_at: tx.time || new Date(),
        date: tx.time || new Date(),
        invoiceId: invoiceId,
        invoice_id: invoiceId,
        billNo: tx.ref_no || `INV-${invoiceId}`,
        invoiceNo: tx.ref_no || `INV-${invoiceId}`,
        paymentMethod: tx.payment_method || "Cash"
      };

      setDetailsForReceiptPrint(receiptDetails);
      await printReceipt(receiptDetails);
      toast.success("Receipt sent to printer!");
    } catch (err) {
      toast.dismiss();
      console.error("Print receipt error:", err);
      toast.error("Failed to print receipt");
    }
  };

  return (
    <Page>
      <div className="container-xl py-2">
        {/* TOP BRAND HEADER */}
        <div className="d-print-none mb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-xs">
            <div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-[#70B56A]"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#243922] dark:text-[#70B56A]">
                  Cashier Drawer Session & Audit
                </span>
              </div>
              <h1 className="text-2xl font-black text-zinc-900 dark:text-white mt-0.5 tracking-tight">
                SHIFT CONTROL
              </h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 m-0">
                Opening Float, Drawer Reconciliation, Denominations & POS Audit
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Navigation Tabs */}
              <div className="flex bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl text-xs font-bold">
                <button 
                  onClick={() => setActiveTab('active')}
                  className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'active' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-300 hover:text-black'}`}
                >
                  <IconCash size={15} /> Active Shift
                </button>
                <button 
                  onClick={() => { setActiveTab('closed'); mutateClosed(); }}
                  className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'closed' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-300 hover:text-black'}`}
                >
                  <IconClock size={15} /> Closed Shifts ({closedShifts?.length || 0})
                </button>
                <button 
                  onClick={() => setActiveTab('reports')}
                  className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'reports' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-300 hover:text-black'}`}
                >
                  <IconReceipt size={15} /> Shift Reports
                </button>
              </div>

              {/* Refresh button */}
              <button 
                onClick={() => { mutate(); mutateClosed(); toast.success("Refreshed shift records"); }}
                className="btn btn-sm btn-outline border-zinc-300 hover:border-[#70B56A] hover:bg-[#70B56A]/10 text-zinc-700 dark:text-zinc-300 rounded-xl"
                aria-label="Refresh"
              >
                <IconRefresh size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* TAB 1: ACTIVE SHIFT */}
        {activeTab === 'active' && (
          <div>
            {!shift ? (
              /* SECTION 8: SHIFT OPENING FORM (REVAMPED WITH BRAND THEME) */
              <div className="max-w-2xl mx-auto my-4 bg-white dark:bg-zinc-900 rounded-3xl shadow-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                {/* Brand Header */}
                <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-6 text-center">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-200 text-xs font-semibold mb-2 backdrop-blur-xs">
                    <IconCash size={14} /> Cashier Drawer Initialization
                  </div>
                  <h2 className="text-2xl font-black text-white m-0 tracking-tight">Open Shift Drawer</h2>
                  <p className="text-white/80 text-xs mt-1 max-w-md mx-auto">
                    Count and record physical drawer float to establish audit baseline before taking customer orders.
                  </p>

                  {/* Certified Cashier Badge */}
                  <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-xl bg-black/25 text-xs text-white border border-white/10">
                    <IconUser size={14} className="text-[#70B56A]" />
                    <span>Logged In Staff: <strong className="text-emerald-300">{staffProfileName}</strong> ({staffDesignation})</span>
                  </div>
                </div>

                <div className="p-6">
                  {/* Mode Selector */}
                  <div className="flex bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl mb-4 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setOpenShiftMode('notes')}
                      className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all ${openShiftMode === 'notes' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-400 hover:text-black'}`}
                    >
                      <IconCalculator size={15} /> Count Notes in Drawer
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenShiftMode('quick')}
                      className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all ${openShiftMode === 'quick' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-400 hover:text-black'}`}
                    >
                      <IconCoins size={15} /> Quick Float Amount
                    </button>
                  </div>

                  {/* QUICK FLOAT MODE */}
                  {openShiftMode === 'quick' && (
                    <div className="mb-4">
                      <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1.5 block">
                        Direct Opening Cash Float ({currency}):
                      </label>
                      <input 
                        type="number"
                        step="any"
                        min="0"
                        placeholder="Enter total opening float amount"
                        className="input input-bordered w-full rounded-xl text-lg font-bold text-zinc-900 dark:text-white"
                        value={openingCashManual}
                        onChange={(e) => setOpeningCashManual(e.target.value)}
                      />
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-3xs font-bold uppercase text-zinc-400 self-center mr-1">Quick:</span>
                        {quickPresets.map(preset => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setOpeningCashManual(String(preset))}
                            className="btn btn-xs btn-outline border-zinc-200 hover:border-[#70B56A] hover:bg-[#70B56A]/10 text-xs rounded-lg font-mono"
                          >
                            +{preset.toLocaleString()}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setOpeningCashManual('0')}
                          className="btn btn-xs btn-ghost text-rose-600 hover:bg-rose-50 text-xs rounded-lg"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {/* DENOMINATIONS TABLE MODE */}
                  {openShiftMode === 'notes' && (
                    <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden mb-4">
                      <div className="bg-zinc-50 dark:bg-zinc-800/60 px-4 py-2 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center text-xs font-bold text-zinc-600 dark:text-zinc-300">
                        <span>Denomination Note</span>
                        <span className="text-center">Count / Stepper</span>
                        <span className="text-end">Subtotal ({currency})</span>
                      </div>
                      <div className="divide-y divide-zinc-100 dark:divide-zinc-800 max-h-[320px] overflow-y-auto">
                        {denominationList.map(val => (
                          <div key={val} className="px-4 py-2 flex items-center justify-between text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                            <div className="font-bold text-zinc-800 dark:text-zinc-200 w-20">
                              {currency} {val}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleDenomStep(val, -1, false)}
                                className="w-6 h-6 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 flex items-center justify-center text-zinc-600"
                              >
                                <IconMinus size={12} />
                              </button>
                              <input 
                                type="number" 
                                min="0"
                                className="input input-xs input-bordered w-16 text-center font-bold font-mono rounded-md"
                                value={openingDenoms[val] || ''}
                                placeholder="0"
                                onChange={(e) => handleDenomChange(val, e.target.value, false)}
                              />
                              <button
                                type="button"
                                onClick={() => handleDenomStep(val, 1, false)}
                                className="w-6 h-6 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 flex items-center justify-center text-zinc-600"
                              >
                                <IconPlus size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDenomStep(val, 5, false)}
                                className="px-1.5 h-6 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 text-3xs font-mono font-bold text-zinc-600"
                                aria-label="+5 Notes"
                              >
                                +5
                              </button>
                            </div>
                            <div className="text-end font-mono font-bold text-zinc-800 dark:text-zinc-200 w-24">
                              {((openingDenoms[val] || 0) * val).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* GRAND TOTAL SUMMARY BANNER */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-[#1e3a1d] to-[#243922] text-white flex justify-between items-center shadow-md mb-4">
                    <div>
                      <span className="text-3xs uppercase tracking-widest text-emerald-300 font-bold block">
                        Establish Drawer Float
                      </span>
                      <span className="text-sm font-bold text-white">Total Physical Cash</span>
                    </div>
                    <div className="text-2xl font-black font-mono text-emerald-300">
                      {currency} {calculatedOpeningCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Notes / Drawer Identifier */}
                  <div className="mb-4">
                    <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1 block">
                      Drawer / Shift Opening Note (Optional):
                    </label>
                    <input 
                      type="text"
                      className="input input-sm input-bordered w-full rounded-xl text-xs"
                      placeholder="e.g. Morning shift counter float, verified by manager"
                      value={openingNote}
                      onChange={(e) => setOpeningNote(e.target.value)}
                    />
                  </div>

                  {/* Confirm & Open Button */}
                  <button 
                    type="button"
                    className="btn w-full bg-[#70B56A] hover:bg-[#629c5d] text-white font-bold text-sm rounded-xl border-0 shadow-md flex items-center justify-center gap-2 py-3"
                    disabled={isOpeningSubmitting}
                    onClick={handleOpenShift}
                  >
                    <IconPlayerPlay size={18} />
                    {isOpeningSubmitting ? 'Opening Shift Drawer...' : 'CONFIRM & OPEN SHIFT DRAWER'}
                  </button>
                </div>
              </div>
            ) : (
              /* ACTIVE SHIFT DASHBOARD */
              <div className="space-y-4">
                {/* Active Shift Header Card */}
                <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 flex items-center justify-center text-emerald-700 dark:text-emerald-300 shrink-0 shadow-xs">
                        <IconCash size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-black text-zinc-900 dark:text-white m-0 tracking-tight">
                            SHIFT #{shift.id}
                          </h2>
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 animate-pulse">
                            ● ACTIVE IN PROGRESS
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                          <span>Opened: <strong className="text-zinc-900 dark:text-zinc-200">{new Date(shift.opened_at).toLocaleString()}</strong></span>
                          <span>Cashier: <strong className="text-indigo-600 dark:text-indigo-400">{shift.cashier_name || staffProfileName}</strong></span>
                          <span>Opening Float: <strong className="text-[#243922] dark:text-[#70B56A]">{currency} {openingCash.toFixed(2)}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <button 
                        onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_open', shift: shift })}
                        className="btn btn-sm btn-outline border-zinc-300 hover:border-[#70B56A] hover:bg-[#70B56A]/10 text-zinc-700 dark:text-zinc-300 rounded-xl flex items-center gap-1.5"
                      >
                        <IconPrinter size={15} /> Print Float Slip
                      </button>
                      <button 
                        onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_close', shift: shift })}
                        className="btn btn-sm btn-outline border-zinc-300 hover:border-[#70B56A] hover:bg-[#70B56A]/10 text-zinc-700 dark:text-zinc-300 rounded-xl flex items-center gap-1.5"
                      >
                        <IconReceipt size={15} /> Print Z-Report
                      </button>
                      <button 
                        onClick={() => setIsCloseModalOpen(true)}
                        className="btn btn-sm bg-rose-600 hover:bg-rose-700 text-white border-0 font-bold rounded-xl shadow-xs flex items-center gap-1.5 px-4"
                      >
                        <IconPlayerStop size={15} /> Close Shift & Reconcile
                      </button>
                    </div>
                  </div>
                </div>

                {/* KPI & DRAWER RECONCILIATION SUMMARY */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* PAYMENT COLLECTION MATRIX */}
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center mb-3 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                        <h3 className="text-xs font-black uppercase tracking-wider text-[#243922] dark:text-[#70B56A] m-0">
                          Payment Mode Collections
                        </h3>
                        <span className="text-3xs text-zinc-400">Click 👁 to drill down</span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-zinc-50 dark:bg-zinc-800 text-zinc-500 text-3xs uppercase font-bold border-b border-zinc-200 dark:border-zinc-700">
                              <th className="py-2 px-3 text-left">Payment Mode</th>
                              <th className="py-2 px-3 text-right">Amount ({currency})</th>
                              <th className="py-2 px-3 text-center">Tx Count</th>
                              <th className="py-2 px-3 text-center w-14">View</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 font-medium">
                            {payments.map((p, idx) => (
                              <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                                <td className="py-2.5 px-3 font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full bg-[#70B56A]"></span>
                                  {p.payment_method}
                                </td>
                                <td className="py-2.5 px-3 text-right font-mono font-bold text-zinc-900 dark:text-white">
                                  {Number(p.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className="px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 font-mono text-3xs font-bold">
                                    {p.tx_count || 1}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <button 
                                    className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-950 transition-colors"
                                    aria-label={`View ${p.payment_method} Transactions`}
                                    onClick={() => openPaymentDrillDown(p)}
                                  >
                                    <IconEye size={15} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {payments.length === 0 && (
                              <tr>
                                <td colSpan={4} className="py-4 text-center text-zinc-400">No collections logged yet for this shift.</td>
                              </tr>
                            )}
                            <tr className="bg-emerald-50/70 dark:bg-emerald-950/40 border-t-2 border-[#70B56A] font-bold text-xs">
                              <td className="py-2.5 px-3 text-[#243922] dark:text-[#70B56A]">Total Collected</td>
                              <td className="py-2.5 px-3 text-right font-mono text-[#243922] dark:text-[#70B56A]">
                                {currency} {payments.reduce((acc, p) => acc + Number(p.total || 0), 0).toFixed(2)}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Supplementary indicators */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-800 text-3xs">
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2 rounded-xl">
                        <div className="text-zinc-400 uppercase font-bold">Sales Due</div>
                        <div className="font-mono font-bold text-amber-600 mt-0.5">{currency} {Number(invoices.due_sales || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2 rounded-xl">
                        <div className="text-zinc-400 uppercase font-bold">Discount</div>
                        <div className="font-mono font-bold text-rose-600 mt-0.5">-{currency} {Number(invoices.total_discount || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2 rounded-xl">
                        <div className="text-zinc-400 uppercase font-bold">Complementary</div>
                        <div className="font-mono font-bold text-purple-600 mt-0.5">{currency} {Number(invoices.total_complementary || 0).toFixed(2)}</div>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2 rounded-xl">
                        <div className="text-zinc-400 uppercase font-bold">VAT / Tax</div>
                        <div className="font-mono font-bold text-zinc-900 dark:text-white mt-0.5">+{currency} {Number(invoices.total_tax || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* CASH DRAWER RECONCILIATION SUMMARY */}
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center mb-3 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                        <h3 className="text-xs font-black uppercase tracking-wider text-[#243922] dark:text-[#70B56A] m-0">
                          Cash Drawer Physical Reconciliation
                        </h3>
                        <span className="text-3xs text-zinc-400">Cash Flow Only</span>
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between py-1.5 px-3 rounded-lg bg-zinc-50 dark:bg-zinc-800">
                          <span className="text-zinc-600 dark:text-zinc-400">Opening Cash Float</span>
                          <span className="font-mono font-bold text-indigo-700 dark:text-indigo-400">+{currency} {openingCash.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                          <span className="text-zinc-600 dark:text-zinc-400">+ Cash Sales Revenue</span>
                          <span className="font-mono font-bold text-emerald-600">+{currency} {cashSales.toFixed(2)}</span>
                        </div>
                        {otherCashIn > 0 && (
                          <div className="flex justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                            <span className="text-zinc-600 dark:text-zinc-400">+ Other Cash Inflow</span>
                            <span className="font-mono font-bold text-emerald-600">+{currency} {otherCashIn.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                          <span className="text-zinc-600 dark:text-zinc-400">- Cash Expenses Paid</span>
                          <span className="font-mono font-bold text-rose-600">-{currency} {cashExpenses.toFixed(2)}</span>
                        </div>
                        {cashPurchases > 0 && (
                          <div className="flex justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                            <span className="text-zinc-600 dark:text-zinc-400">- Cash Purchases</span>
                            <span className="font-mono font-bold text-rose-600">-{currency} {cashPurchases.toFixed(2)}</span>
                          </div>
                        )}
                        {cashRefunds > 0 && (
                          <div className="flex justify-between py-1.5 px-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                            <span className="text-zinc-600 dark:text-zinc-400">- Cash Refunds</span>
                            <span className="font-mono font-bold text-rose-600">-{currency} {cashRefunds.toFixed(2)}</span>
                          </div>
                        )}

                        {/* Grand Expected Drawer Cash */}
                        <div className="p-3.5 rounded-xl bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white flex justify-between items-center mt-3 shadow-md">
                          <div>
                            <span className="text-3xs uppercase tracking-widest text-emerald-300 font-bold block">
                              Expected Cash in Hand
                            </span>
                            <span className="text-sm font-bold text-white">Drawer Physical Target</span>
                          </div>
                          <div className="text-2xl font-black font-mono text-emerald-300">
                            {currency} {expectedDrawerCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-3xs text-zinc-400 mt-3 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                      * Excludes non-cash transactions (Card, QR, Credit dues). Reconcile drawer cash with physical count at shift closure.
                    </div>
                  </div>
                </div>

                {/* SHIFT TRANSACTIONS AUDIT LOG */}
                <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 pb-3 border-b border-zinc-100 dark:border-zinc-800">
                    <div>
                      <h3 className="text-base font-black text-zinc-900 dark:text-white m-0">
                        Shift Transactions Audit Log
                      </h3>
                      <p className="text-xs text-zinc-500 m-0">
                        Detailed log of sales, expenses, and invoices issued during Shift #{shift.id}
                      </p>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex flex-wrap gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl text-3xs font-bold">
                      {['all', 'sales', 'cash', 'qr', 'card', 'due', 'expenses', 'discount', 'complimentary', 'refunds'].map(tab => (
                        <button 
                          key={tab}
                          onClick={() => setTxFilter(tab)}
                          className={`px-2.5 py-1 rounded-lg uppercase transition-all ${txFilter === tab ? 'bg-[#70B56A] text-white shadow-xs font-bold' : 'text-zinc-600 dark:text-zinc-400 hover:text-black'}`}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Actions bar */}
                  <div className="flex items-center justify-between gap-2 mb-3 text-xs flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">Showing <strong>{filteredTransactions.length}</strong> transactions</span>
                      {selectedTx.size > 0 && (
                        <span className="badge badge-sm bg-[#70B56A] text-white font-bold">
                          {selectedTx.size} Selected
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button 
                        disabled={selectedTx.size === 0}
                        onClick={() => {
                          toast.success(`Printing receipt for ${selectedTx.size} selected transactions`);
                          window.print();
                        }}
                        className="btn btn-xs btn-outline border-zinc-300 rounded-lg text-xs"
                      >
                        <IconPrinter size={13} /> Print Selected ({selectedTx.size})
                      </button>
                      <button 
                        onClick={() => window.print()}
                        className="btn btn-xs btn-outline border-zinc-300 rounded-lg text-xs"
                      >
                        Print Full Table
                      </button>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-zinc-50 dark:bg-zinc-800 text-zinc-500 text-3xs uppercase font-bold border-b border-zinc-200 dark:border-zinc-700">
                          <th className="py-2.5 px-3 text-center w-10">
                            <input 
                              type="checkbox" 
                              className="checkbox checkbox-xs rounded"
                              checked={selectedTx.size === filteredTransactions.length && filteredTransactions.length > 0}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th className="py-2.5 px-3 text-left">Time</th>
                          <th className="py-2.5 px-3 text-left">Invoice / Ref</th>
                          <th className="py-2.5 px-3 text-left">Type</th>
                          <th className="py-2.5 px-3 text-left">Payment Mode</th>
                          <th className="py-2.5 px-3 text-right">Amount ({currency})</th>
                          <th className="py-2.5 px-3 text-center w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                        {filteredTransactions.map(tx => (
                          <tr key={tx.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                            <td className="py-2.5 px-3 text-center">
                              <input 
                                type="checkbox" 
                                className="checkbox checkbox-xs rounded"
                                checked={selectedTx.has(tx.id)}
                                onChange={() => toggleSelectTx(tx.id)}
                              />
                            </td>
                            <td className="py-2.5 px-3 text-zinc-500 font-mono text-3xs">
                              {tx.time ? new Date(tx.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="py-2.5 px-3 font-mono font-bold text-zinc-900 dark:text-white">
                              {tx.ref_no || tx.invoice_no || `#${tx.id}`}
                            </td>
                            <td className="py-2.5 px-3">
                              <span className={`px-2 py-0.5 rounded-full text-3xs font-bold uppercase ${tx.type === 'Expense' ? 'bg-rose-100 text-rose-800' : tx.type === 'Refund' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                {tx.type || 'Sale'}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-zinc-700 dark:text-zinc-300 font-medium">
                              {tx.payment_method || 'Cash'}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-zinc-900 dark:text-white">
                              {Number(tx.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button 
                                  className="p-1 rounded-lg text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-950 transition-colors"
                                  aria-label="View Invoice Breakdown"
                                  onClick={() => handleViewInvoice(tx)}
                                >
                                  <IconEye size={15} />
                                </button>
                                <button 
                                  className="p-1 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                                  aria-label="Print Thermal Receipt"
                                  onClick={() => handlePrintTransaction(tx)}
                                >
                                  <IconPrinter size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredTransactions.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-6 text-center text-zinc-400">
                              No transactions recorded for the selected filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: CLOSED SHIFTS HISTORY */}
        {activeTab === 'closed' && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-zinc-100 dark:border-zinc-800">
              <div>
                <h3 className="text-lg font-black text-zinc-900 dark:text-white m-0">
                  Closed Shifts & Cashier History
                </h3>
                <p className="text-xs text-zinc-500 m-0">
                  Audited shift records, physical drawer float counts, variances and reconciliation
                </p>
              </div>
              <button 
                onClick={() => mutateClosed()}
                className="btn btn-sm btn-outline border-zinc-300 hover:border-[#70B56A] rounded-xl text-xs flex items-center gap-1.5"
              >
                <IconRefresh size={14} /> Refresh List
              </button>
            </div>

            <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-xl">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800 text-zinc-500 text-3xs uppercase font-bold border-b border-zinc-200 dark:border-zinc-700">
                    <th className="py-2.5 px-3 text-left">Shift #</th>
                    <th className="py-2.5 px-3 text-left">Cashier</th>
                    <th className="py-2.5 px-3 text-left">Opened</th>
                    <th className="py-2.5 px-3 text-left">Closed</th>
                    <th className="py-2.5 px-3 text-right">Opening Float</th>
                    <th className="py-2.5 px-3 text-right">Closing Cash</th>
                    <th className="py-2.5 px-3 text-right">Expected Drawer</th>
                    <th className="py-2.5 px-3 text-right">Difference</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                    <th className="py-2.5 px-3 text-center w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 font-medium">
                  {(closedShifts || []).map(s => {
                    const diff = Number(s.cash_difference || 0);
                    const isBal = Math.abs(diff) < 0.01;
                    return (
                      <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                        <td className="py-2.5 px-3 font-mono font-bold text-zinc-900 dark:text-white">
                          #{s.id}
                        </td>
                        <td className="py-2.5 px-3 font-bold text-zinc-800 dark:text-zinc-200">
                          {s.cashier_name || 'Staff'}
                        </td>
                        <td className="py-2.5 px-3 text-zinc-500 text-3xs font-mono">
                          {new Date(s.opened_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="py-2.5 px-3 text-zinc-500 text-3xs font-mono">
                          {s.closed_at ? new Date(s.closed_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Active'}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono">
                          {currency} {Number(s.opening_cash || 0).toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-bold text-zinc-900 dark:text-white">
                          {currency} {Number(s.closing_cash || 0).toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono">
                          {currency} {Number(s.expected_cash || 0).toFixed(2)}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-mono font-bold ${diff < 0 ? 'text-rose-600' : diff > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {currency} {diff.toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-3xs font-bold ${isBal ? 'bg-emerald-100 text-emerald-800' : diff < 0 ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
                            {isBal ? '✓ BALANCED' : diff < 0 ? 'SHORT' : 'OVER'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Eye button opens modal directly! */}
                            <button 
                              className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-950 transition-colors"
                              aria-label="Inspect Shift Reconciliation Audit"
                              onClick={() => setSelectedShiftForDetails(s)}
                            >
                              <IconEye size={16} />
                            </button>
                            <button 
                              className="p-1.5 rounded-lg text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800 transition-colors"
                              aria-label="Print Shift Close (Z-Report)"
                              onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_close', shift: s })}
                            >
                              <IconPrinter size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {(!closedShifts || closedShifts.length === 0) && (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-zinc-400">
                        No closed shifts found in database records.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: SHIFT REPORTS */}
        {activeTab === 'reports' && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 shadow-xs">
            <h3 className="text-lg font-black text-zinc-900 dark:text-white mb-2">
              Shift Reports & Thermal Statements
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              Select any shift from below to generate official 80mm thermal receipts or full A4 financial audit statements.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(closedShifts || []).slice(0, 9).map(s => (
                <div key={s.id} className="p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50 hover:border-[#70B56A] transition-all flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-mono font-bold text-sm text-zinc-900 dark:text-white">Shift #{s.id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-3xs font-bold ${Math.abs(Number(s.cash_difference || 0)) < 0.01 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {Math.abs(Number(s.cash_difference || 0)) < 0.01 ? 'BALANCED' : 'VARIANCE'}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-600 dark:text-zinc-400 space-y-1">
                      <div>Cashier: <strong className="text-zinc-800 dark:text-zinc-200">{s.cashier_name || 'Staff'}</strong></div>
                      <div>Opened: <span>{new Date(s.opened_at).toLocaleDateString()}</span></div>
                      <div>Actual Cash: <strong className="font-mono text-emerald-700">{currency} {Number(s.closing_cash || 0).toFixed(2)}</strong></div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-700">
                    <button 
                      onClick={() => setSelectedShiftForDetails(s)}
                      className="btn btn-xs flex-1 btn-outline border-zinc-300 hover:border-[#70B56A] rounded-lg"
                    >
                      <IconEye size={13} /> View Audit
                    </button>
                    <button 
                      onClick={() => setPrintModalConfig({ isOpen: true, reportType: 'shift_close', shift: s })}
                      className="btn btn-xs flex-1 bg-[#70B56A] hover:bg-[#629c5d] text-white border-0 rounded-lg"
                    >
                      <IconPrinter size={13} /> Print Z-Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MODAL 1: CLOSING SHIFT & DRAWER RECONCILIATION */}
        {isCloseModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsCloseModalOpen(false)}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 max-w-4xl w-full overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-200 text-xs font-semibold mb-1">
                    <IconCash size={14} /> End of Shift Reconciliation
                  </div>
                  <h3 className="text-xl font-black text-white m-0 tracking-tight">
                    Close Shift #{shift?.id} & Drawer Reconciliation
                  </h3>
                  <p className="text-xs text-white/80 m-0 mt-0.5">
                    Cashier: <strong className="text-emerald-300">{staffProfileName}</strong> ({staffDesignation})
                  </p>
                </div>
                <button 
                  onClick={() => setIsCloseModalOpen(false)}
                  className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* LEFT: DENOMINATIONS / ACTUAL CASH */}
                  <div>
                    {/* Segment switcher */}
                    <div className="flex bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl mb-3 text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => setCloseShiftMode('notes')}
                        className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1 transition-all ${closeShiftMode === 'notes' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-400'}`}
                      >
                        <IconCalculator size={14} /> Count Notes in Drawer
                      </button>
                      <button
                        type="button"
                        onClick={() => setCloseShiftMode('quick')}
                        className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1 transition-all ${closeShiftMode === 'quick' ? 'bg-[#70B56A] text-white shadow-xs' : 'text-zinc-600 dark:text-zinc-400'}`}
                      >
                        <IconCoins size={14} /> Enter Actual Total
                      </button>
                    </div>

                    {closeShiftMode === 'quick' ? (
                      <div className="space-y-3">
                        <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 block">
                          Total Actual Physical Cash in Drawer ({currency}):
                        </label>
                        <input 
                          type="number"
                          step="any"
                          placeholder="e.g. 15420.00"
                          className="input input-bordered w-full rounded-xl text-lg font-bold font-mono text-zinc-900 dark:text-white"
                          value={closingCashManual}
                          onChange={(e) => setClosingCashManual(e.target.value)}
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {quickPresets.map(preset => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setClosingCashManual(String(preset))}
                              className="btn btn-xs btn-outline border-zinc-200 hover:border-[#70B56A] text-xs rounded-lg font-mono"
                            >
                              +{preset.toLocaleString()}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => setClosingCashManual('0')}
                            className="btn btn-xs btn-ghost text-rose-600 text-xs rounded-lg"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden">
                        <div className="bg-zinc-50 dark:bg-zinc-800/60 px-3 py-1.5 border-b border-zinc-200 dark:border-zinc-800 flex justify-between items-center text-xs font-bold text-zinc-600 dark:text-zinc-300">
                          <span>Note</span>
                          <span className="text-center">Count / Stepper</span>
                          <span className="text-end">Amount ({currency})</span>
                        </div>
                        <div className="divide-y divide-zinc-100 dark:divide-zinc-800 max-h-[260px] overflow-y-auto">
                          {denominationList.map(val => (
                            <div key={val} className="px-3 py-1.5 flex items-center justify-between text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                              <span className="font-bold text-zinc-800 dark:text-zinc-200 w-16">{val}</span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleDenomStep(val, -1, true)}
                                  className="w-6 h-6 rounded bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 flex items-center justify-center text-zinc-600"
                                >
                                  <IconMinus size={12} />
                                </button>
                                <input 
                                  type="number"
                                  min="0"
                                  className="input input-xs input-bordered w-14 text-center font-bold font-mono rounded"
                                  value={closingDenoms[val] || ''}
                                  placeholder="0"
                                  onChange={(e) => handleDenomChange(val, e.target.value, true)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleDenomStep(val, 1, true)}
                                  className="w-6 h-6 rounded bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 flex items-center justify-center text-zinc-600"
                                >
                                  <IconPlus size={12} />
                                </button>
                              </div>
                              <span className="text-end font-mono font-bold text-zinc-800 dark:text-zinc-200 w-20">
                                {((closingDenoms[val] || 0) * val).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3">
                      <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300 mb-1 block">
                        Closing Reconciliation Note:
                      </label>
                      <input 
                        type="text"
                        className="input input-sm input-bordered w-full rounded-xl text-xs"
                        placeholder="e.g. Drawer verified with manager, exact change counted"
                        value={closingNotes}
                        onChange={(e) => setClosingNotes(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* RIGHT: RECONCILIATION AUDIT STATUS */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-zinc-500 mb-3">
                        Drawer Audit Comparison
                      </h4>

                      <div className="bg-zinc-50 dark:bg-zinc-800/70 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-700 space-y-2.5 text-xs mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-600 dark:text-zinc-400">Opening Float:</span>
                          <span className="font-mono font-bold text-zinc-900 dark:text-white">+{currency} {openingCash.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-600 dark:text-zinc-400">+ Cash Sales:</span>
                          <span className="font-mono font-bold text-emerald-600">+{currency} {cashSales.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-600 dark:text-zinc-400">- Cash Expenses:</span>
                          <span className="font-mono font-bold text-rose-600">-{currency} {cashExpenses.toFixed(2)}</span>
                        </div>
                        <hr className="my-1 border-zinc-200 dark:border-zinc-700" />
                        <div className="flex justify-between items-center font-bold">
                          <span className="text-zinc-700 dark:text-zinc-300">Expected Drawer Cash:</span>
                          <span className="font-mono text-indigo-700 dark:text-indigo-400">{currency} {expectedDrawerCash.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center font-bold text-sm">
                          <span className="text-zinc-900 dark:text-white">Actual Physical Count:</span>
                          <span className="font-mono text-[#70B56A]">{currency} {actualDrawerCash.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Variance Status Banner */}
                      <div className={`p-4 rounded-2xl text-center border font-bold text-xs ${isBalanced ? 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300' : isShort ? 'bg-rose-50 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300' : 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300'}`}>
                        {isBalanced && (
                          <div className="flex items-center justify-center gap-1.5 text-sm">
                            <IconCheck size={18} /> DRAWER 100% BALANCED ({currency} 0.00)
                          </div>
                        )}
                        {isShort && (
                          <div>
                            <div className="flex items-center justify-center gap-1.5 text-sm">
                              <IconAlertTriangle size={18} /> CASH SHORTAGE DETECTED
                            </div>
                            <div className="font-mono text-base mt-1">-{currency} {Math.abs(cashDifference).toFixed(2)}</div>
                          </div>
                        )}
                        {isOver && (
                          <div>
                            <div className="flex items-center justify-center gap-1.5 text-sm">
                              <IconCoins size={18} /> CASH OVERAGE DETECTED
                            </div>
                            <div className="font-mono text-base mt-1">+{currency} {cashDifference.toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                      <button 
                        type="button" 
                        onClick={() => setIsCloseModalOpen(false)}
                        className="btn btn-outline border-zinc-300 flex-1 rounded-xl text-xs"
                      >
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        disabled={isClosingSubmitting}
                        onClick={handleCloseShiftSubmit}
                        className="btn bg-[#70B56A] hover:bg-[#629c5d] text-white flex-1 rounded-xl text-xs font-bold border-0 shadow-md flex items-center justify-center gap-1.5"
                      >
                        <IconPlayerStop size={16} />
                        {isClosingSubmitting ? 'Closing Shift...' : 'CONFIRM & CLOSE SHIFT'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 2: SHIFT DETAILS MODAL (When clicking 👁 on any shift) */}
        {selectedShiftForDetails && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setSelectedShiftForDetails(null)}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 max-w-3xl w-full overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-200 text-xs font-semibold mb-1">
                    <IconReceipt size={14} /> Shift Statement & Audit
                  </div>
                  <h3 className="text-xl font-black text-white m-0 tracking-tight">
                    Shift #{selectedShiftForDetails.id} Audit Inspection
                  </h3>
                  <p className="text-xs text-white/80 m-0 mt-0.5">
                    Cashier: <strong className="text-emerald-300">{selectedShiftForDetails.cashier_name || 'Staff'}</strong> | Status: <span className="uppercase font-bold">{selectedShiftForDetails.closed_at ? 'Closed' : 'Active'}</span>
                  </p>
                </div>
                <button 
                  onClick={() => setSelectedShiftForDetails(null)}
                  className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* 4 Metric cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase text-zinc-400 font-bold block">Opening Float</span>
                    <span className="text-base font-black font-mono text-zinc-900 dark:text-white mt-1 block">
                      {currency} {Number(selectedShiftForDetails.opening_cash || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase text-zinc-400 font-bold block">Closing Cash</span>
                    <span className="text-base font-black font-mono text-emerald-700 dark:text-emerald-400 mt-1 block">
                      {currency} {Number(selectedShiftForDetails.closing_cash || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase text-zinc-400 font-bold block">Expected Drawer</span>
                    <span className="text-base font-black font-mono text-indigo-700 dark:text-indigo-400 mt-1 block">
                      {currency} {Number(selectedShiftForDetails.expected_cash || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className={`p-3 rounded-2xl border ${Math.abs(Number(selectedShiftForDetails.cash_difference || 0)) < 0.01 ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-rose-50 border-rose-300 text-rose-800'}`}>
                    <span className="text-3xs uppercase font-bold block">Discrepancy</span>
                    <span className="text-base font-black font-mono mt-1 block">
                      {currency} {Number(selectedShiftForDetails.cash_difference || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-3 text-xs bg-zinc-50 dark:bg-zinc-800/60 p-3 rounded-xl border border-zinc-200 dark:border-zinc-700">
                  <div>
                    <span className="text-zinc-500 block">Session Opened:</span>
                    <span className="font-bold text-zinc-900 dark:text-white font-mono">
                      {new Date(selectedShiftForDetails.opened_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">Session Closed:</span>
                    <span className="font-bold text-zinc-900 dark:text-white font-mono">
                      {selectedShiftForDetails.closed_at ? new Date(selectedShiftForDetails.closed_at).toLocaleString() : 'Currently Active'}
                    </span>
                  </div>
                </div>

                {/* Print and Close Actions */}
                <div className="flex gap-2 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex-wrap">
                  <button 
                    onClick={() => {
                      setPrintModalConfig({ isOpen: true, reportType: 'shift_open', shift: selectedShiftForDetails });
                      setSelectedShiftForDetails(null);
                    }}
                    className="btn btn-sm btn-outline border-zinc-300 hover:border-[#70B56A] rounded-xl flex-1 text-xs flex items-center justify-center gap-1.5"
                  >
                    <IconPrinter size={15} /> Print Float Slip
                  </button>
                  <button 
                    onClick={() => {
                      setPrintModalConfig({ isOpen: true, reportType: 'shift_close', shift: selectedShiftForDetails });
                      setSelectedShiftForDetails(null);
                    }}
                    className="btn btn-sm bg-[#70B56A] hover:bg-[#629c5d] text-white border-0 rounded-xl flex-1 text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs"
                  >
                    <IconReceipt size={15} /> Print Shift Z-Report
                  </button>
                  <button 
                    onClick={() => setSelectedShiftForDetails(null)}
                    className="btn btn-sm btn-outline border-zinc-300 rounded-xl text-xs"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 3: INVOICE DETAILS MODAL (When clicking 👁 on any transaction) */}
        {viewingInvoice && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setViewingInvoice(null)}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 max-w-3xl w-full overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-200 text-xs font-semibold mb-1">
                    <IconReceipt size={14} /> Tax Invoice Breakdown
                  </div>
                  <h3 className="text-xl font-black text-white m-0 tracking-tight">
                    Invoice #{viewingInvoice.invoiceNo || viewingInvoice.ref_no || viewingInvoice.id}
                  </h3>
                  <p className="text-xs text-white/80 m-0 mt-0.5 font-mono">
                    {viewingInvoice.created_at || viewingInvoice.date || viewingInvoice.time 
                      ? new Date(viewingInvoice.created_at || viewingInvoice.date || viewingInvoice.time).toLocaleString() 
                      : '-'}
                  </p>
                </div>
                <button 
                  onClick={() => setViewingInvoice(null)}
                  className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* 4 Metadata Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase font-bold text-zinc-400 block">Customer</span>
                    <span className="font-bold text-zinc-900 dark:text-white truncate block mt-0.5">
                      {viewingInvoice.customer_name || 'Walk-in Customer'}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase font-bold text-zinc-400 block">Payment Mode</span>
                    <span className="font-bold text-[#70B56A] block mt-0.5">
                      {viewingInvoice.payment_method || viewingInvoice.payment_method_name || 'Cash'}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase font-bold text-zinc-400 block">Payment Status</span>
                    <span className={`font-bold uppercase text-3xs px-2 py-0.5 rounded-full inline-block mt-0.5 ${viewingInvoice.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {viewingInvoice.payment_status || (Number(viewingInvoice.due_amount || 0) > 0 ? 'due' : 'paid')}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                    <span className="text-3xs uppercase font-bold text-zinc-400 block">Token / Ref</span>
                    <span className="font-mono font-bold text-zinc-900 dark:text-white block mt-0.5">
                      {viewingInvoice.token_no ? `#${viewingInvoice.token_no}` : (viewingInvoice.ref_no || '-')}
                    </span>
                  </div>
                </div>

                {/* Items table */}
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-zinc-500 mb-2">Itemized Items Breakdown</h4>
                  <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-zinc-50 dark:bg-zinc-800 text-zinc-500 text-3xs uppercase font-bold border-b border-zinc-200 dark:border-zinc-700">
                          <th className="py-2 px-3 text-left">#</th>
                          <th className="py-2 px-3 text-left">Item Description</th>
                          <th className="py-2 px-3 text-center">Qty</th>
                          <th className="py-2 px-3 text-right">Price ({currency})</th>
                          <th className="py-2 px-3 text-right">Total ({currency})</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 font-medium">
                        {viewingInvoice.orders && viewingInvoice.orders.length > 0 ? (
                          viewingInvoice.orders.flatMap(o => o.items || []).map((it, idx) => (
                            <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                              <td className="py-2 px-3 text-zinc-400">{idx + 1}</td>
                              <td className="py-2 px-3">
                                <div className="font-bold text-zinc-900 dark:text-white">{it.item_title || it.title}</div>
                                {it.variant_title && <div className="text-3xs text-zinc-500">Variant: {it.variant_title}</div>}
                                {it.addons && it.addons.length > 0 && (
                                  <div className="text-3xs text-zinc-500">
                                    Addons: {it.addons.map(a => a.title || a.name).join(', ')}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center font-mono">{it.quantity}</td>
                              <td className="py-2 px-3 text-right font-mono">{Number(it.price || 0).toFixed(2)}</td>
                              <td className="py-2 px-3 text-right font-mono font-bold text-zinc-900 dark:text-white">
                                {Number((it.price * it.quantity) || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        ) : viewingInvoice.item_name ? (
                          <tr>
                            <td className="py-2 px-3">1</td>
                            <td className="py-2 px-3 font-bold">{viewingInvoice.item_name}</td>
                            <td className="py-2 px-3 text-center">{viewingInvoice.quantity || 1}</td>
                            <td className="py-2 px-3 text-right">{Number(viewingInvoice.price || 0).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-bold">{Number(viewingInvoice.total || 0).toFixed(2)}</td>
                          </tr>
                        ) : (
                          <tr>
                            <td className="py-2 px-3">1</td>
                            <td className="py-2 px-3 font-bold">Order Summary Items</td>
                            <td className="py-2 px-3 text-center">1</td>
                            <td className="py-2 px-3 text-right">{Number(viewingInvoice.subtotal || viewingInvoice.amount || 0).toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-bold">{Number(viewingInvoice.subtotal || viewingInvoice.amount || 0).toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Financial Totals Breakdown */}
                <div className="flex justify-end">
                  <div className="w-full sm:w-72 bg-zinc-50 dark:bg-zinc-800 p-3.5 rounded-2xl border border-zinc-200 dark:border-zinc-700 text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Subtotal:</span>
                      <span className="font-mono font-bold">{currency} {Number(viewingInvoice.subtotal !== undefined ? viewingInvoice.subtotal : (viewingInvoice.sub_total || viewingInvoice.amount || 0)).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.discount || 0) > 0 && (
                      <div className="flex justify-between text-rose-600">
                        <span>Discount:</span>
                        <span className="font-mono font-bold">-{currency} {Number(viewingInvoice.discount).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">VAT / Tax:</span>
                        <span className="font-mono font-bold">+{currency} {Number(viewingInvoice.taxTotal || viewingInvoice.tax_total).toFixed(2)}</span>
                      </div>
                    )}
                    <hr className="my-1 border-zinc-200 dark:border-zinc-700" />
                    <div className="flex justify-between font-bold text-sm">
                      <span className="text-zinc-900 dark:text-white">Total Amount:</span>
                      <span className="font-mono text-[#70B56A]">{currency} {Number(viewingInvoice.total || viewingInvoice.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-700 font-bold">
                      <span>Paid Amount:</span>
                      <span className="font-mono">{currency} {Number(viewingInvoice.paidAmount !== undefined ? viewingInvoice.paidAmount : (viewingInvoice.paid_amount !== undefined ? viewingInvoice.paid_amount : (viewingInvoice.total || viewingInvoice.amount || 0))).toFixed(2)}</span>
                    </div>
                    {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : (viewingInvoice.due_amount || 0)) > 0 && (
                      <div className="flex justify-between text-amber-600 font-bold">
                        <span>Due Amount:</span>
                        <span className="font-mono">{currency} {Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : viewingInvoice.due_amount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-3 border-t border-zinc-100 dark:border-zinc-800">
                  <button 
                    onClick={() => setViewingInvoice(null)}
                    className="btn btn-sm btn-outline border-zinc-300 rounded-xl text-xs"
                  >
                    Close
                  </button>
                  <button 
                    onClick={() => handlePrintTransaction(viewingInvoice)}
                    className="btn btn-sm bg-[#70B56A] hover:bg-[#629c5d] text-white border-0 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs"
                  >
                    <IconPrinter size={15} /> Print Thermal Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 4: DRILL-DOWN AUDIT MODAL (When clicking 👁 on payment modes) */}
        {drillModal.isOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 max-w-4xl w-full overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-emerald-200 text-xs font-semibold mb-1">
                    <IconSearch size={14} /> Itemized Audit Log
                  </div>
                  <h3 className="text-xl font-black text-white m-0 tracking-tight">
                    {drillModal.title}
                  </h3>
                  <p className="text-xs text-white/80 m-0 mt-0.5">
                    Filter: <strong className="text-emerald-300">{drillModal.filter}</strong> | Total: <strong className="font-mono text-emerald-300">{currency} {Number(drillModal.total || 0).toFixed(2)}</strong>
                  </p>
                </div>
                <button 
                  onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
                  className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Search Filter Bar */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <IconSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input 
                      type="text"
                      className="input input-sm input-bordered w-full pl-9 rounded-xl text-xs"
                      placeholder="Search by invoice #, customer name, amount..."
                      value={drillSearch}
                      onChange={(e) => setDrillSearch(e.target.value)}
                    />
                  </div>
                  <span className="text-xs text-zinc-500 whitespace-nowrap">
                    <strong>{filteredDrillItems.length}</strong> records
                  </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-xl max-h-[380px]">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-zinc-50 dark:bg-zinc-800 text-zinc-500 text-3xs uppercase font-bold border-b border-zinc-200 dark:border-zinc-700">
                      <tr>
                        <th className="py-2 px-3 text-left">Time</th>
                        <th className="py-2 px-3 text-left">Invoice / Ref</th>
                        <th className="py-2 px-3 text-left">Customer</th>
                        <th className="py-2 px-3 text-left">Type</th>
                        <th className="py-2 px-3 text-right">Amount ({currency})</th>
                        <th className="py-2 px-3 text-center w-20">Inspect</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 font-medium">
                      {filteredDrillItems.map(item => (
                        <tr key={item.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                          <td className="py-2 px-3 font-mono text-3xs text-zinc-500">
                            {item.time ? new Date(item.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="py-2 px-3 font-mono font-bold text-zinc-900 dark:text-white">
                            {item.ref_no || item.invoice_no || `#${item.id}`}
                          </td>
                          <td className="py-2 px-3 text-zinc-700 dark:text-zinc-300">
                            {item.customer_name || 'Walk-in Customer'}
                          </td>
                          <td className="py-2 px-3">
                            <span className="px-2 py-0.5 rounded-full text-3xs font-bold uppercase bg-emerald-100 text-emerald-800">
                              {item.type || 'Sale'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-zinc-900 dark:text-white">
                            {Number(item.amount || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button 
                              className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-950 transition-colors"
                              aria-label="Inspect Full Invoice"
                              onClick={() => handleViewInvoice(item)}
                            >
                              <IconEye size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredDrillItems.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-6 text-center text-zinc-400">
                            No records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end pt-2">
                  <button 
                    onClick={() => setDrillModal({ isOpen: false, title: '', items: [] })}
                    className="btn btn-sm btn-outline border-zinc-300 rounded-xl text-xs"
                  >
                    Close Modal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PRINT MODAL (Shift Open & Shift Close Z-Report) */}
        {printModalConfig.isOpen && (
          <FinancialReportsPrintModal 
            isOpen={printModalConfig.isOpen}
            onClose={() => setPrintModalConfig({ isOpen: false, reportType: 'shift_close', shift: null })}
            reportType={printModalConfig.reportType}
            selectedShift={printModalConfig.shift || shift}
            data={{
              payments: summary?.payments || [],
              transactions: summary?.transactions || [],
              invoices: summary?.invoices || {},
              summary: summary || {}
            }}
          />
        )}

      </div>
    </Page>
  );
}
